import { BACKEND_URL } from "./lib/backend";
/**
 * App.js — thin router shell.
 * All page components live in src/pages/, all shared components in src/components/.
 * P3-2 refactor: extracted PublicLanding → LandingPage, FeedPage → FeedPage, CookieBanner → CookieBanner.
 */
import React, { useState } from "react";
import { RouteCanonical } from "./lib/seo";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AuthProvider, useAuth } from "./AuthContext";
import { useDarkMode } from "./hooks/useDarkMode";
import { RefreshCw } from "lucide-react";

// Pages
import LandingPage from "./pages/LandingPage";
import FeedPage from "./pages/FeedPage";
import ReportsPage from "./pages/ReportsPage";
import PodcastPage from "./pages/PodcastPage";
import AdminDashboard from "./AdminDashboard";
import TravelBriefPage from "./TravelBriefPage";
import BriefArchivePage from "./BriefArchivePage";
import PublicNewsletterPage from "./PublicNewsletterPage";
import NewsletterArchivePage from "./pages/NewsletterArchivePage";
import SubscribePage from "./pages/SubscribePage";
import AboutPage from "./pages/AboutPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import AskPage from "./pages/AskPage";
import InsightsPage from "./pages/InsightsPage";
import LegalPageFromStatic from "./pages/LegalPageFromStatic";
import NotFoundPage from "./pages/NotFoundPage";
import OpenAppPage from "./pages/OpenAppPage";
import ProPage from "./pages/ProPage";
import McpPage from "./pages/McpPage";
import McpManagePage from "./pages/McpManagePage";
import OfficinaPage from "./pages/OfficinaPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import GlossaryPage from "./pages/GlossaryPage";
import AiStatsPage from "./pages/AiStatsPage";
import OfficinaArticlePage from "./pages/OfficinaArticlePage";
import ReportBriefPage from "./pages/ReportBriefPage";
import ReportHubPage from "./pages/ReportHubPage";
import MobileAppBanner from "./components/MobileAppBanner";
import ConnectionBanner from "./components/ConnectionBanner";
import AuthPage from "./AuthPage";
import VerifyPage from "./VerifyPage";
import DirectLoginPage from "./DirectLoginPage";

// Pulse by TourismIntel (M2a)
import PulsePage from "./pulse/PulsePage";
import CountryPulse from "./pulse/CountryPulse";
import ComparePage from "./pulse/ComparePage";
import RankingsPage from "./pulse/RankingsPage";
import CitiesPage from "./pulse/CitiesPage";
import ItalyDeepDive from "./pulse/ItalyDeepDive";
import AskDataPage from "./pulse/AskDataPage";
import PulseGate from "./pulse/PulseGate";
import ForecastAccuracyPage from "./pulse/ForecastAccuracyPage";
import ItalyLanding from "./pulse/ItalyLanding";

/** /pulse/italy: anonymous visitors get the public landing (no redirect), users the deep dive. */
function ItalyRoute() {
  const { user, loading } = useAuth();
  if (loading) return <PulseGate><ItalyDeepDive /></PulseGate>;
  return user ? <ItalyDeepDive /> : <ItalyLanding />;
}

// Shared components
import CookieBanner from "./components/CookieBanner";

/* ===== Google Analytics 4 — SPA pageview tracker =====
   The gtag snippet in public/index.html initializes GA4 with
   `send_page_view: false`, so we're responsible for emitting a page_view
   event on every React Router navigation. Fires once on mount + on every
   pathname/search change; skipped in dev if window.gtag hasn't loaded
   (e.g. ad blocker, no network). */
function GoogleAnalyticsRouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}

/* ===== ROUTE GUARDS ===== */
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-[#006874]" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-[#006874]" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/feed" replace />;
  return children;
}

/* ===== UNSUBSCRIBE PAGE ===== */
function UnsubscribePage() {
  const [emailInput, setEmailInput] = useState("");
  const [status, setStatus] = useState("idle");
  const API = BACKEND_URL + "/api";
  const token = new URLSearchParams(window.location.search).get("token");

  // One-click confirm from an emailed link (proof of ownership).
  React.useEffect(() => {
    if (!token) return;
    setStatus("loading");
    fetch(`${API}/auth/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"));
  }, [token, API]);

  // Manual email entry: request a confirmation link (never deactivates directly).
  const handleRequestLink = async () => {
    if (!emailInput.trim()) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/auth/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      if (!res.ok && res.status !== 200) throw new Error("Error");
      setStatus("sent");
    } catch (e) { setStatus("error"); }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-10 text-center" style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12), 0 4px 24px rgba(25,28,29,0.04)" }} data-testid="unsubscribe-card">
        <p className="category-tag mb-2 text-[#bac9cc]">Manage subscription</p>
        <h1 className="editorial-heading text-3xl font-medium mb-2 text-[#191c1d]">Unsubscribe</h1>
        {token ? (
          <>
            {status === "loading" && <p className="text-sm text-[#6b7a7d] mb-8">Processing your request…</p>}
            {status === "success" && <div data-testid="unsub-token-success" className="rounded-xl bg-green-50 border border-green-200 p-4 mb-6 text-green-700 text-sm">You have been unsubscribed. Your platform access has been revoked.</div>}
            {status === "error" && <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-6 text-red-700 text-sm">This link is invalid or has expired. Request a new one below.</div>}
          </>
        ) : (
          <>
            <p className="text-sm text-[#6b7a7d] mb-8 leading-relaxed">Enter your email and we&apos;ll send you a link to confirm unsubscribing from TourismIntel. This also revokes your platform access.</p>
            {status === "sent" && <div data-testid="unsub-link-sent" className="rounded-xl bg-green-50 border border-green-200 p-4 mb-6 text-green-700 text-sm">If an account exists for that email, we&apos;ve sent a confirmation link. Please check your inbox.</div>}
            {status === "error" && <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-6 text-red-700 text-sm">Something went wrong. Please try again or contact mirko@bereadyfor.ai</div>}
            {status !== "sent" && (
              <>
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="your@email.com" data-testid="unsub-email-input" className="w-full border border-[#e8ecee] rounded-xl px-4 py-3 text-sm text-[#191c1d] placeholder:text-[#bac9cc] outline-none focus:border-[#006874] mb-4 transition-colors" />
                <button onClick={handleRequestLink} disabled={status === "loading" || !emailInput.trim()} data-testid="unsub-request-btn" className="w-full py-3 rounded-xl bg-[#006874] text-white text-sm font-semibold disabled:opacity-50 transition-all hover:bg-[#004f58]">
                  {status === "loading" ? "Processing…" : "Send confirmation link"}
                </button>
              </>
            )}
          </>
        )}
        <Link to="/" className="block mt-6 text-xs text-[#bac9cc] hover:text-[#6b7a7d]">← Back to TourismIntel</Link>
      </div>
    </div>
  );
}

/* ===== REDIRECT HELPERS ===== */
function RootRedirect() {
  const { user, loading } = useAuth();
  const [dm] = useDarkMode();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]"><RefreshCw className="w-5 h-5 animate-spin text-[#006874]" /></div>;
  if (user) return <Navigate to="/feed" replace />;
  return <LandingPage darkMode={dm} />;
}

/** Auth pages get the user's site-wide theme preference */
function ThemedPage({ Comp }) {
  const [dm] = useDarkMode();
  return <Comp darkMode={dm} />;
}

function PreviewBriefRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/travel-brief/${slug}`} replace />;
}

/**
 * MobileAppBannerForPublicRoutes — show the install/open-app banner only on
 * the public content pages where we want to push app adoption. We use
 * prefix matching so detail pages (e.g. /newsletter/:id, /travel-brief/:slug)
 * are also covered.
 */
const PUBLIC_BANNER_PREFIXES = [
  "/briefs",
  "/travel-brief",
  "/podcast",
  "/newsletters",
  "/newsletter/",
  "/reports",
  "/about",
];

function MobileAppBannerForPublicRoutes() {
  const { pathname } = useLocation();
  const match = PUBLIC_BANNER_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?")
  );
  if (!match) return null;
  return <MobileAppBanner />;
}

/* ===== APP ===== */
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <RouteCanonical />
          <GoogleAnalyticsRouteTracker />
          <ConnectionBanner />
          <CookieBanner />
          <MobileAppBannerForPublicRoutes />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/auth" element={<ThemedPage Comp={AuthPage} />} />
            <Route path="/auth/verify" element={<ThemedPage Comp={VerifyPage} />} />
            <Route path="/auth/direct" element={<ThemedPage Comp={DirectLoginPage} />} />
            <Route path="/feed" element={<RequireAuth><FeedPage /></RequireAuth>} />
            {/* Pulse — /pulse is public (teaser for anonymous, World Pulse for
                authed); deeper routes are client-gated with next persistence. */}
            <Route path="/pulse" element={<PulsePage />} />
            <Route path="/pulse/country/:iso3" element={<PulseGate><CountryPulse /></PulseGate>} />
            <Route path="/pulse/italy" element={<ItalyRoute />} />
            <Route path="/pulse/forecast-accuracy" element={<PulseGate><ForecastAccuracyPage /></PulseGate>} />
            <Route path="/pulse/ask" element={<PulseGate><AskDataPage /></PulseGate>} />
            <Route path="/pulse/compare" element={<PulseGate><ComparePage /></PulseGate>} />
            <Route path="/pulse/rankings" element={<PulseGate><RankingsPage /></PulseGate>} />
            <Route path="/pulse/cities" element={<PulseGate><CitiesPage /></PulseGate>} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/category/:slug" element={<ReportHubPage kind="category" />} />
            <Route path="/reports/organisation/:slug" element={<ReportHubPage kind="organisation" />} />
            <Route path="/reports/:slug" element={<ReportBriefPage />} />
            <Route path="/podcast" element={<PodcastPage />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            {/* Legal pages — Apple App Store mandates these be public + readable.
                LegalPageFromStatic fetches /<slug>/index.html (the canonical
                static HTML in /public/{terms,privacy,support}/index.html) and
                renders it via document.write so the page's own <head> takes over.
                When Emergent's static-routing layer is fixed, the static file
                will be served directly without the SPA shell intercept and this
                React route stops being hit. Single source of truth = the static HTML. */}
            <Route path="/terms"   element={<LegalPageFromStatic slug="terms"   />} />
            <Route path="/privacy" element={<LegalPageFromStatic slug="privacy" />} />
            {/* GSC iter115: /privacy-policy was crawled as a soft-404 duplicate */}
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            <Route path="/support" element={<LegalPageFromStatic slug="support" />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/travel-brief" element={<TravelBriefPage />} />
            <Route path="/travel-brief/:slug" element={<TravelBriefPage />} />
            <Route path="/preview/brief/:slug" element={<PreviewBriefRedirect />} />
            <Route path="/briefs" element={<BriefArchivePage />} />
            <Route path="/newsletter/:newsletterId" element={<PublicNewsletterPage />} />
          <Route path="/newsletters" element={<NewsletterArchivePage />} />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/pro" element={<ProPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/app/open" element={<OpenAppPage />} />
            <Route path="/developers" element={<RequireAuth><ApiDocsPage /></RequireAuth>} />
            <Route path="/mcp" element={<McpPage />} />
            <Route path="/mcp/manage" element={<McpManagePage />} />
            <Route path="/officina-turistica" element={<OfficinaPage />} />
            <Route path="/officina-turistica/:slug" element={<OfficinaArticlePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug" element={<EventDetailPage />} />
            <Route path="/glossary" element={<GlossaryPage />} />
            <Route path="/ai-travel-statistics" element={<AiStatsPage />} />
            <Route path="/ask" element={<RequireAuth><AskPage /></RequireAuth>} />
            <Route path="/insights" element={<RequireAuth><InsightsPage /></RequireAuth>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
